import {groupByUniqueKey} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessFlowchartEdgeConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import {
  EdgeWithData,
  FlowchartActionFunction,
  NodeWithData,
  WithFlowchartActionFunctions,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {convertNodeForReactFlow} from 'processes/graph-builder/flowchart-viz/util/ConvertFlowchartForReactFlowUtil';
import {
  ActionFunctionsPerInteractiveNodeType,
  ProcessFlowchartEdgeConnection,
  ProcessFlowchartEdgeConnectionType,
  ProcessFlowchartEdgeType,
  ProcessFlowchartInteractiveEdge,
  ProcessFlowchartInteractiveNode,
  ProcessFlowchartRenderData,
  ProcessInteractiveFlowchart,
  PROCESS_FLOWCHART_EDGE_LABEL_OPTIONS,
  processMarkerEndOptions,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  getClientIdForProcessFlowchartEdge,
  getNodeTypeForProcessInteractiveFlowchart,
  getTargetHandleIdForEdge,
} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';

import {isOutputNode, isRootNode, isRouterNode} from './getNodeDetailsTypeForInteractiveNode';
import validateProcessInteractiveFlowchart from './validateProcessInteractiveFlowchart';

// TODO(BNCH-46514): Implement a function to determine if edges should render as LOYSP or parallel
export function getConnectionDataForIncomingEdges(
  node: ProcessFlowchartInteractiveNode,
  incomingEdges: Array<ProcessFlowchartInteractiveEdge>
): ProcessFlowchartEdgeConnection {
  // This will temporarily render all edges as LOYSP (single handle) opposed to as parallel (multiple handles)
  return {
    type: ProcessFlowchartEdgeConnectionType.SINGLE,
    incomingEdges: incomingEdges.map((edge) => edge.clientId),
  };
}

function getActionFunctionsForEdge(
  edge: ProcessFlowchartInteractiveEdge,
  allActionFunctions: Array<
    FlowchartActionFunction<ProcessFlowchartInteractiveEdge, ProcessInteractiveFlowchart>
  >
): Array<FlowchartActionFunction<ProcessFlowchartInteractiveEdge, ProcessInteractiveFlowchart>> {
  if (edge.hasBeenUsedToCreateData) {
    return [];
  }
  return allActionFunctions;
}

function getActionFunctionsForNode(
  node: ProcessFlowchartInteractiveNode,
  allActionFunctions: ActionFunctionsPerInteractiveNodeType
): Array<FlowchartActionFunction<ProcessFlowchartInteractiveNode, ProcessInteractiveFlowchart>> {
  if (node.statusCounts != null && node.statusCounts.length > 0) {
    return [];
  } else if (isOutputNode(node)) {
    return [];
  } else if (isRootNode(node)) {
    return [];
  }
  return allActionFunctions[node.type];
}

export default function convertInteractiveProcessFlowchartForReactFlow(
  flowchart: ProcessInteractiveFlowchart,
  locked: boolean,
  designSpace: ProcessFlowchartDesignSpace,
  nodeActionFunctions: ActionFunctionsPerInteractiveNodeType,
  edgeActionFunctions: Array<
    FlowchartActionFunction<ProcessFlowchartInteractiveEdge, ProcessInteractiveFlowchart>
  >,
  didInitialFlowchartHaveChainedRouters: boolean
): ProcessFlowchartRenderData {
  let boundingYTop: number = Infinity;
  let boundingYBottom: number = 0;
  const nodes: Array<NodeWithData<WithFlowchartActionFunctions<ProcessFlowchartInteractiveNode>>> = [];
  const edges: Array<EdgeWithData<WithFlowchartActionFunctions<ProcessFlowchartInteractiveEdge>>> = [];
  const nodeIdToEdgeConnectionData: {[nodeClientId: string]: ProcessFlowchartEdgeConnection} = {};

  const validatedFlowchart = validateProcessInteractiveFlowchart(
    flowchart,
    designSpace,
    didInitialFlowchartHaveChainedRouters
  );
  for (const node of validatedFlowchart.nodes) {
    const position = node.layout != null ? node.layout.position : {x: 0, y: 0};
    const incomingEdgeConnection = getConnectionDataForIncomingEdges(
      node,
      validatedFlowchart.edges.filter((edge) => edge.toNodeClientId === node.clientId)
    );
    nodeIdToEdgeConnectionData[node.clientId] = incomingEdgeConnection;
    nodes.push({
      ...convertNodeForReactFlow(
        {
          ...node,
          actionFunctions: getActionFunctionsForNode(node, nodeActionFunctions),
          incomingEdgeConnection,
          locked,
        },
        position
      ),
      type: getNodeTypeForProcessInteractiveFlowchart(node, designSpace.flowchartSchema.id),
    });
    boundingYBottom = Math.max(boundingYBottom, position.y);
    boundingYTop = Math.min(boundingYTop, position.y);
  }

  const nodeById = groupByUniqueKey(validatedFlowchart.nodes, ({clientId}) => clientId);
  const doesEdgeMatchEdgeReference = (
    edge: ProcessFlowchartInteractiveEdge,
    sourceNodeId: string,
    edgeReference?: ProcessFlowchartEdgeConfigSpec | null
  ): boolean => {
    if (edgeReference === undefined) {
      return false;
    } else if (edgeReference == null) {
      return edge.fromNodeClientId === sourceNodeId && isOutputNode(nodeById[edge.toNodeClientId]);
    }
    return edge.clientId === edgeReference.clientId;
  };

  for (const edge of validatedFlowchart.edges) {
    const edgeHasErrors = (edge.errors?.length ?? 0) > 0;
    const edgeTargetConnectionData = nodeIdToEdgeConnectionData[edge.toNodeClientId];
    assert(edgeTargetConnectionData != null, 'Expected to find inbound edges for node', {
      validatedFlowchart,
      nodeIdToEdgeConnectionData,
      edge,
    });
    const sourceNode = nodeById[edge.fromNodeClientId];
    let edgeLabel: string | undefined;
    if (sourceNode != null && isRouterNode(sourceNode)) {
      const functionSpec = [
        ...sourceNode.routerConfig.functionSpecs,
        sourceNode.routerConfig.defaultFunctionSpec,
      ].find((functionSpec) =>
        doesEdgeMatchEdgeReference(edge, sourceNode.clientId, functionSpec?.edgeConfigReference)
      );
      edgeLabel = functionSpec?.name;
    }

    edges.push({
      id: getClientIdForProcessFlowchartEdge(edge.fromNodeClientId, edge.toNodeClientId),
      data: {
        ...edge,
        actionFunctions: getActionFunctionsForEdge(edge, edgeActionFunctions),
        locked,
        layout: edge.layout,
      },
      source: edge.fromNodeClientId,
      /** Parallel edges with the same target node have different target handles - we specify the target
       * handle ID, which is used to identify the handle on the node renderer * */
      targetHandle:
        edgeTargetConnectionData.type === ProcessFlowchartEdgeConnectionType.MULTIPLE &&
        edgeTargetConnectionData.incomingEdges.length > 1
          ? getTargetHandleIdForEdge(edge.clientId)
          : null,
      target: edge.toNodeClientId,
      type: ProcessFlowchartEdgeType.INTERACTIVE_BUILDER,
      markerEnd: processMarkerEndOptions(edgeHasErrors),
      style: {strokeWidth: '1.5px'},
      label: edgeLabel,
      ...PROCESS_FLOWCHART_EDGE_LABEL_OPTIONS,
    });
  }

  return {nodes, edges, centersBoundingBox: {top: boundingYTop, bottom: boundingYBottom}};
}
